import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    FormControlLabel,
    Switch,
    IconButton,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Divider,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Save as SaveIcon,
    PlayArrow as ExecuteIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';

import { Workflow, NodeType, WorkflowStep } from '../../types';
import FacebookStepConfig from '../../components/FacebookStepConfig';

import './style.css';

interface ApiWorkflow {
    id: string;
    name: string;
    description: string;
    enabled: boolean;
    steps: WorkflowStep[];
    schedule?: string;
    createdAt: string;
    updatedAt: string;
}

const convertToWorkflow = (apiWorkflow: ApiWorkflow): Workflow => ({
    id: apiWorkflow.id,
    name: apiWorkflow.name,
    description: apiWorkflow.description,
    isActive: apiWorkflow.enabled,
    enabled: apiWorkflow.enabled,
    schedule: apiWorkflow.schedule || '',
    createdAt: apiWorkflow.createdAt,
    updatedAt: apiWorkflow.updatedAt,
    steps: apiWorkflow.steps,
    nodes: apiWorkflow.steps.map((step, index) => ({
        id: `node-${index}`,
        type: step.type,
        name: step.name,
        description: step.description || '',
        config: step.config || {},
        position: { x: 100 + index * 200, y: 100 },
        data: { label: step.name, type: step.type }
    })),
    edges: [] // Simplified: no edges needed in the new design
});

const initialWorkflow: Workflow = {
    id: '',
    name: '',
    description: '',
    isActive: false,
    enabled: false,
    steps: [],
    nodes: [],
    edges: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    schedule: ''
};

export default function WorkflowEditor() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [workflow, setWorkflow] = useState<Workflow>(initialWorkflow);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    
    // Facebook post configuration
    const [postConfig, setPostConfig] = useState<Record<string, any>>({});

    useEffect(() => {
        // Reset state when component mounts or id changes
        if (id) {
            console.log('Component mounted or ID changed, fetching workflow:', id);
            // Reset to default state first to avoid stale data
            setWorkflow(initialWorkflow);
            fetchWorkflow();
        } else {
            // For new workflow creation, initialize with default values
            console.log('Creating new workflow');
            setWorkflow(initialWorkflow);
        }
    }, [id]);

    const fetchWorkflow = async () => {
        try {
            setLoading(true);
            console.log(`Fetching workflow with id: ${id}`);

            // Get the current authenticated session
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();

            if (!idToken) {
                console.error('No authentication token available');
                setError('Authentication error. Please sign in again.');
                return;
            }

            console.log('Using auth token for request');

            // Get the API endpoint and normalize it
            const apiEndpoint = import.meta.env.VITE_API_ENDPOINT?.replace(/\/$/, '') || '';
            
            // Extract the base URL and stage name from the API endpoint
            const apiParts = apiEndpoint.split('/');
            const stageName = apiParts[apiParts.length - 1] === 'prod' ? 'prod' : '';
            const baseUrl = stageName ? apiEndpoint.replace(`/${stageName}`, '') : apiEndpoint;
            
            // Construct the URL with the appropriate stage name
            const url = stageName 
                ? `${baseUrl}/${stageName}/workflows/${id}`
                : `${apiEndpoint}/workflows/${id}`;
                
            console.log(`Making request to: ${url}`);

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': idToken
                }
            });

            console.log('Response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`HTTP error! status: ${response.status}`, errorText);
                throw new Error(`HTTP error! status: ${response.status}. Details: ${errorText}`);
            }

            const data = await response.json();
            console.log('Raw API response:', data);

            // Validate API response has required fields
            if (!data || !data.id || !data.name || !Array.isArray(data.steps)) {
                throw new Error('Invalid workflow data received');
            }

            const apiWorkflow = data as ApiWorkflow;
            const convertedWorkflow = convertToWorkflow(apiWorkflow);
            console.log('Converted workflow:', convertedWorkflow);

            // Set the workflow state with all the data
            setWorkflow({
                ...convertedWorkflow,
                isActive: apiWorkflow.enabled,
                schedule: apiWorkflow.schedule || ''
            });

            // If there's a Facebook post step, set its config
            const facebookPostStep = apiWorkflow.steps.find(step => 
                step.type === NodeType.FACEBOOK_CREATE_POST
            );
            
            if (facebookPostStep && facebookPostStep.config) {
                setPostConfig(facebookPostStep.config);
            }

            setError(null);
        } catch (error) {
            console.error('Error fetching workflow:', error);
            setError(`Failed to load workflow: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        // Validate required fields
        if (!workflow.name.trim()) {
            setError('Workflow name is required');
            return;
        }

        try {
            setIsSaving(true);

            // Get the current authenticated session
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();

            if (!idToken) {
                console.error('No authentication token available');
                setError('Authentication error. Please sign in again.');
                return;
            }

            console.log('Using auth token for save request');

            // Create a step for Facebook post creation
            const steps: WorkflowStep[] = [{
                id: `facebook-post-${Date.now()}`,
                type: NodeType.FACEBOOK_CREATE_POST,
                name: 'Create Facebook Post',
                description: 'Creates a post on Facebook',
                config: postConfig
            }];

            const workflowData = {
                name: workflow.name,
                description: workflow.description,
                enabled: workflow.isActive,
                steps: steps,
                schedule: workflow.schedule
            };

            console.log('Sending workflow data:', workflowData);

            // Get the API endpoint and normalize it
            const apiEndpoint = import.meta.env.VITE_API_ENDPOINT?.replace(/\/$/, '') || '';
            
            // Extract the base URL and stage name from the API endpoint
            const apiParts = apiEndpoint.split('/');
            const stageName = apiParts[apiParts.length - 1] === 'prod' ? 'prod' : '';
            const baseUrl = stageName ? apiEndpoint.replace(`/${stageName}`, '') : apiEndpoint;
            
            // Construct the URL with the appropriate stage name
            let url;
            if (id) {
                url = stageName 
                    ? `${baseUrl}/${stageName}/workflows/${id}`
                    : `${apiEndpoint}/workflows/${id}`;
                console.log(`Updating workflow at: ${url}`);
            } else {
                url = stageName 
                    ? `${baseUrl}/${stageName}/workflows`
                    : `${apiEndpoint}/workflows`;
                console.log(`Creating new workflow at: ${url}`);
            }

            let response;

            if (id) {
                console.log(`Updating workflow with id: ${id}`);
                response = await fetch(url, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': idToken
                    },
                    body: JSON.stringify(workflowData)
                });
            } else {
                console.log('Creating new workflow');
                response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': idToken
                    },
                    body: JSON.stringify(workflowData)
                });
            }

            console.log('Response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`HTTP error! status: ${response.status}`, errorText);
                throw new Error(`HTTP error! status: ${response.status}. Details: ${errorText}`);
            }

            const data = await response.json();
            console.log('Response data:', data);

            // Validate response
            if (!data || !data.id) {
                throw new Error('Invalid response data');
            }

            navigate('/workflows');
        } catch (error) {
            console.error('Error saving workflow:', error);
            setError(`Failed to save workflow: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleExecute = async () => {
        try {
            setLoading(true);

            // Get the current authenticated session
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();

            if (!idToken) {
                console.error('No authentication token available for execution');
                setError('Authentication error. Please sign in again.');
                return;
            }

            // Get the API endpoint and normalize it
            const apiEndpoint = import.meta.env.VITE_API_ENDPOINT?.replace(/\/$/, '') || '';
            
            // Extract the base URL and stage name from the API endpoint
            const apiParts = apiEndpoint.split('/');
            const stageName = apiParts[apiParts.length - 1] === 'prod' ? 'prod' : '';
            const baseUrl = stageName ? apiEndpoint.replace(`/${stageName}`, '') : apiEndpoint;
            
            // Construct the URL with the appropriate stage name
            const url = stageName 
                ? `${baseUrl}/${stageName}/executions`
                : `${apiEndpoint}/executions`;
                
            console.log(`Executing workflow at: ${url}`);

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': idToken
                },
                body: JSON.stringify({ workflowId: id })
            });

            console.log('Execution response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`HTTP error! status: ${response.status}`, errorText);
                throw new Error(`HTTP error! status: ${response.status}. Details: ${errorText}`);
            }

            navigate('/executions');
        } catch (error) {
            console.error('Error executing workflow:', error);
            setError(`Failed to execute workflow: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    };

    // Function to refresh the authentication token
    const refreshAuthToken = async () => {
        try {
            console.log('Attempting to refresh authentication token');
            const { fetchAuthSession } = await import('aws-amplify/auth');

            // Force a refresh of the current session
            const session = await fetchAuthSession({ forceRefresh: true });
            const idToken = session.tokens?.idToken?.toString();

            if (idToken) {
                console.log('Authentication token refreshed successfully');
                setError(null);

                // If we have an ID, refetch the workflow with the new token
                if (id) {
                    fetchWorkflow();
                }

                return true;
            } else {
                console.error('Failed to refresh token - no token returned');
                setError('Unable to refresh authentication. Please sign out and sign in again.');
                return false;
            }
        } catch (error) {
            console.error('Error refreshing authentication:', error);
            setError(`Authentication refresh failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
            return false;
        }
    };

    // Handle Facebook step config changes
    const handleConfigChange = (config: Record<string, any>) => {
        setPostConfig(config);
    };

    if (loading && !workflow.id) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading workflow...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <IconButton
                    onClick={() => navigate('/workflows')}
                    sx={{ mr: 2 }}
                >
                    <BackIcon />
                </IconButton>
                <Typography variant="h4" component="h1">
                    {id ? 'Edit Workflow' : 'Create Workflow'}
                </Typography>

                <Box sx={{ display: 'flex', marginLeft: 'auto' }}>
                    <Tooltip title="Refresh authentication token">
                        <IconButton
                            onClick={refreshAuthToken}
                            disabled={loading || isSaving}
                            sx={{ mr: 1 }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>

                    {id && (
                        <Button
                            startIcon={<ExecuteIcon />}
                            onClick={handleExecute}
                            sx={{ mr: 2 }}
                            disabled={loading || isSaving}
                        >
                            Execute
                        </Button>
                    )}

                    <Button
                        startIcon={<SaveIcon />}
                        variant="contained"
                        onClick={handleSave}
                        disabled={loading || isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                {/* Workflow Details */}
                <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 calc(40% - 16px)' } }}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Workflow Details
                        </Typography>
                        <TextField
                            fullWidth
                            label="Name"
                            value={workflow.name}
                            onChange={(e) => setWorkflow({ ...workflow, name: e.target.value })}
                            margin="normal"
                            error={!workflow.name.trim() && error?.includes('name')}
                            helperText={!workflow.name.trim() && error?.includes('name') ? "Name is required" : ""}
                            required
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={workflow.description}
                            onChange={(e) => setWorkflow({ ...workflow, description: e.target.value })}
                            margin="normal"
                            multiline
                            rows={3}
                        />
                        <TextField
                            fullWidth
                            label="Schedule (cron expression)"
                            value={workflow.schedule}
                            onChange={(e) => setWorkflow({ ...workflow, schedule: e.target.value })}
                            margin="normal"
                            placeholder="0 0 * * *"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={workflow.isActive}
                                    onChange={(e) => setWorkflow({ ...workflow, isActive: e.target.checked })}
                                />
                            }
                            label="Active"
                            sx={{ mt: 2 }}
                        />
                    </Paper>
                </Box>

                {/* Facebook Post Configuration */}
                <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 calc(60% - 16px)' } }}>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Facebook Post Configuration
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            
                            <FacebookStepConfig
                                type={NodeType.FACEBOOK_CREATE_POST}
                                config={postConfig}
                                onChange={handleConfigChange}
                            />
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </Box>
    );
}

// Add Tooltip component to avoid TypeScript errors
const Tooltip: React.FC<{ title: string, children: React.ReactElement }> = ({ title, children }) => {
    return React.cloneElement(children, {
        title: title,
    });
};
